import { useMemo } from "react";
import { Check, X } from "lucide-react";

/**
 * Real-time password complexity + confirmation checklist (rule C1).
 * Pure presentational component — parents own the input fields.
 *
 * Props:
 *   password: current password value
 *   confirm: current confirm value (optional — confirmation row hidden if undefined)
 *   onValidChange?: (boolean) => void  // fires when overall validity flips
 */
export function passwordRules(password = "") {
  return {
    length: password.length >= 10,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /\d/.test(password),
  };
}

export function passwordIsValid(password = "") {
  const r = passwordRules(password);
  return r.length && r.uppercase && r.lowercase && r.digit;
}

const RULES_FR = [
  { id: "length", label: "Au moins 10 caractères" },
  { id: "uppercase", label: "Au moins 1 majuscule" },
  { id: "lowercase", label: "Au moins 1 minuscule" },
  { id: "digit", label: "Au moins 1 chiffre" },
];

export default function PasswordChecklist({ password = "", confirm, showConfirmRow = false, lang = "fr" }) {
  const rules = useMemo(() => passwordRules(password), [password]);
  const confirmOk = showConfirmRow && confirm !== undefined && confirm.length > 0 && confirm === password;
  const confirmMismatch = showConfirmRow && confirm !== undefined && confirm.length > 0 && confirm !== password;
  const allValid = passwordIsValid(password) && (!showConfirmRow || confirmOk);

  return (
    <div data-testid="pwd-checklist" className="mt-3 space-y-1.5" aria-live="polite">
      {RULES_FR.map((r) => {
        const ok = rules[r.id];
        return (
          <div
            key={r.id}
            data-testid={`pwd-rule-${r.id}`}
            className={`flex items-center gap-2 text-[11px] leading-5 transition-colors duration-200 ${
              ok ? "text-[#C5A059]" : "text-[#7a756f]"
            }`}
          >
            {ok ? (
              <Check size={13} strokeWidth={2.5} className="flex-shrink-0" />
            ) : (
              <X size={13} strokeWidth={2} className="flex-shrink-0 opacity-60" />
            )}
            <span>{r.label}</span>
          </div>
        );
      })}
      {showConfirmRow && confirm !== undefined && confirm.length > 0 && (
        <div
          data-testid="pwd-rule-confirm"
          className={`flex items-center gap-2 text-[11px] leading-5 pt-1.5 mt-1 border-t border-white/5 ${
            confirmOk ? "text-[#C5A059]" : "text-[#E07A5F]"
          }`}
        >
          {confirmOk ? (
            <Check size={13} strokeWidth={2.5} className="flex-shrink-0" />
          ) : (
            <X size={13} strokeWidth={2} className="flex-shrink-0" />
          )}
          <span>
            {confirmOk
              ? lang === "en" ? "Passwords match" : "Les mots de passe correspondent"
              : lang === "en" ? "Passwords don't match" : "Les mots de passe ne correspondent pas"}
          </span>
        </div>
      )}
      {allValid && !confirmMismatch && (
        <div
          data-testid="pwd-accepted"
          className="flex items-center gap-2 text-[11px] leading-5 pt-1.5 mt-1 border-t border-white/5 text-[#C5A059] font-medium tracking-wide"
        >
          <Check size={13} strokeWidth={2.5} className="flex-shrink-0" />
          <span>{lang === "en" ? "Password accepted" : "Mot de passe accepté"}</span>
        </div>
      )}
    </div>
  );
}
